// src/antdviews/components/UserSidebar.tsx

// 用户侧边栏组件（Ant Design 风格）

import React from 'react'
import { Layout, Menu, Button, Tooltip, Typography } from 'antd'
import type { MenuProps } from 'antd'
import {
    MailOutlined,
    ArrowRightOutlined,
    MenuFoldOutlined,
    MenuUnfoldOutlined,
} from '@ant-design/icons'
import { Page } from '../../services/Page'

const { Sider } = Layout
const { Text } = Typography

// 定义菜单项类型
type MenuItem = Required<MenuProps>['items'][number]

/**
 * UserSidebar 组件的 Props 定义
 */
export interface UserSidebarProps {
    /** 当前选中的菜单 key，用于高亮 */
    selectMenu: string
    /** i18n 翻译函数 */
    trans: (key: string) => string
    /** 侧边栏是否折叠（受控） */
    collapsed?: boolean
    /** 折叠状态变化回调 */
    onCollapse?: (collapsed: boolean) => void
}

interface UserSidebarState {
    /** 内部维护的折叠状态 */
    collapsed: boolean
}

/**
 * 用户侧边栏组件
 * 使用 Ant Design 的 Layout.Sider + Menu 实现
 */
export default class UserSidebar extends React.Component<UserSidebarProps, UserSidebarState> {

    state: UserSidebarState = {
        collapsed: this.props.collapsed ?? false,
    }

    /**
     * 切换侧边栏折叠状态
     */
    private toggleSidebar = (): void => {
        const newCollapsed = !this.state.collapsed
        this.setState({ collapsed: newCollapsed })
        if (this.props.onCollapse) {
            this.props.onCollapse(newCollapsed)
        }
    }

    /**
     * 菜单点击跳转
     */
    private handleMenuClick = (path: string): void => {
        window.location.href = path
    }

    /**
     * 构建菜单项配置
     */
    private getMenuItems = (): MenuItem[] => {
        const { trans } = this.props

        return [
            {
                key: Page.accountMgmt,
                icon: <MailOutlined />,
                label: trans('accountMgmt'),
                onClick: () => this.handleMenuClick(Page.userAccountMgmt),
            },
            {
                key: Page.userDynamic,
                icon: <ArrowRightOutlined />,
                label: trans('myDynamic'),
                onClick: () => this.handleMenuClick(Page.userDynamic),
            },
        ]
    }

    /**
     * 判断当前选中的菜单项
     */
    private getSelectedKeys = (): string[] => {
        const { selectMenu } = this.props
        if (!selectMenu) return []
        const menuItems = this.getMenuItems()
        const hasKey = menuItems.some((item) => item?.key === selectMenu)
        return hasKey ? [selectMenu] : []
    }

    render(): React.ReactNode {
        const { trans, selectMenu } = this.props
        const { collapsed } = this.state

        const menuItems = this.getMenuItems()

        return (
            <Sider
                id="sidebar-left"
                className="sidebar-left"
                width={240}
                collapsed={collapsed}
                collapsedWidth={56}
                theme="light"
                style={{
                    background: 'var(--sidebar-bg)',
                    boxShadow: '2px 0 8px rgba(0, 21, 41, 0.08)',
                    height: 'calc(100vh - 64px)',
                    position: 'sticky',
                    top: 64,
                    overflow: 'auto',
                }}
            >
                {/* 侧边栏头部：标题 + 折叠按钮 */}
                <div
                    className="sidebar-header"
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: collapsed ? '12px 8px' : '16px 20px',
                        borderBottom: '1px solid #f0f0f0',
                        height: 56,
                        transition: 'all 0.2s',
                    }}
                >
                    {!collapsed && (
                        <Text strong style={{ fontSize: 14, color: '#1890ff' }}>
                            {trans('navigation') || 'Navigation'}
                        </Text>
                    )}
                    <Tooltip
                        title={
                            collapsed
                                ? trans('expand') || '展开'
                                : trans('collapse') || '收起'
                        }
                    >
                        <Button
                            type="text"
                            icon={
                                collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />
                            }
                            onClick={this.toggleSidebar}
                            style={{
                                fontSize: 16,
                                width: 32,
                                height: 32,
                                padding: 0,
                                marginLeft: collapsed ? 0 : 'auto',
                            }}
                        />
                    </Tooltip>
                </div>

                {/* 导航菜单 */}
                <Menu
                    mode="inline"
                    theme="light"
                    selectedKeys={this.getSelectedKeys()}
                    defaultSelectedKeys={selectMenu ? [selectMenu] : []}
                    style={{
                        borderRight: 'none',
                        padding: '8px 0',
                    }}
                    items={menuItems}
                />
            </Sider>
        )
    }
}